import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Card, CardContent, useTheme, Paper, IconButton, Collapse, List, ListItem, ListItemIcon, ListItemText, Chip, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { CheckCircleOutline, ExpandMore as ExpandMoreIcon, Spa, QueryStats, EmojiEvents, LightbulbOutlined, RouteOutlined, TrendingUpOutlined, Star, LockOpen, PersonAdd, Assessment, WorkOutline, CommentBank, CommentBankOutlined, PersonAddOutlined, WorkOutlineOutlined, PersonOutlined, EventOutlined, SchoolOutlined, ArrowForward, ExpandMore } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { useAuth } from '../contexts/AuthContext';

type Testimonial = {
  name: string;
  shortQuote: string;
  longQuote: string;
  role: string;
};

const testimonials: Testimonial[] = [
  {
    name: "Yueran Z.",
    shortQuote: "Met Hanneke's hulp ben ik niet langer bang voor mijn carrière na mijn afstuderen.",
    longQuote: "Hanneke was mijn docent voor de Career Development module aan de WUR. Ze is een van de meest enthousiaste instructeurs die ik ooit heb ontmoet. Ze is gepassioneerd over het helpen van studenten bij het ontwikkelen van hun persoonlijke en professionele pad. Van Hanneke heb ik verschillende strategieën geleerd voor mijn carrière-ontwikkeling, zoals hoe ik mezelf kan presenteren op mijn CV, hoe ik STAR kan gebruiken om mijn prestaties te beschrijven in mijn motivatiebrief, en hoe ik mijn LinkedIn-pagina kan ontwikkelen. Belangrijker nog, Hanneke heeft mij en andere studenten geholpen om meer vertrouwen te krijgen in onszelf en onze toekomst. Met haar hulp ben ik niet bang om mijn carrière na mijn afstuderen tegemoet te zien. Ik beveel Hanneke aan iedereen aan die persoonlijk of professioneel advies nodig heeft. Je zult meer leren dan je verwacht!",
    role: "Master Student WUR"
  },
  {
    name: "Tevreden Cliënt",
    shortQuote: "Hanneke's enthousiasme en energie maakten het een zeer prettige en goede cursus.",
    longQuote: "Allereerst wil ik Hanneke bedanken voor het ontwerpen en uitvoeren van deze module. Het was geweldig om haar als mentor te hebben en haar enthousiasme en energie maakten het een zeer prettige en goede cursus. Ik ben erg blij met deze module en de manier waarop Hanneke dit met ons heeft verkend. Hartelijk dank!",
    role: "Cursist"
  }
];

interface ExpandableTestimonialProps {
  testimonial: Testimonial;
}

const ExpandableTestimonial: React.FC<ExpandableTestimonialProps> = ({ testimonial }) => {
  const [expanded, setExpanded] = useState(false);
  const { language } = useLanguage();
  
  // Translations for testimonials
  const translatedTestimonials: {
    nl: Record<string, { shortQuote: string; longQuote: string; role: string }>;
    en: Record<string, { shortQuote: string; longQuote: string; role: string }>;
  } = {
    nl: {
      "Yueran Z.": {
        shortQuote: "Met Hanneke's hulp ben ik niet langer bang voor mijn carrière na mijn afstuderen.",
        longQuote: "Hanneke was mijn docent voor de Career Development module aan de WUR. Ze is een van de meest enthousiaste instructeurs die ik ooit heb ontmoet. Ze is gepassioneerd over het helpen van studenten bij het ontwikkelen van hun persoonlijke en professionele pad. Van Hanneke heb ik verschillende strategieën geleerd voor mijn carrière-ontwikkeling, zoals hoe ik mezelf kan presenteren op mijn CV, hoe ik STAR kan gebruiken om mijn prestaties te beschrijven in mijn motivatiebrief, en hoe ik mijn LinkedIn-pagina kan ontwikkelen. Belangrijker nog, Hanneke heeft mij en andere studenten geholpen om meer vertrouwen te krijgen in onszelf en onze toekomst. Met haar hulp ben ik niet bang om mijn carrière na mijn afstuderen tegemoet te zien. Ik beveel Hanneke aan iedereen aan die persoonlijk of professioneel advies nodig heeft. Je zult meer leren dan je verwacht!",
        role: "Master Student WUR"
      },
      "Tevreden Cliënt": {
        shortQuote: "Hanneke's enthousiasme en energie maakten het een zeer prettige en goede cursus.",
        longQuote: "Allereerst wil ik Hanneke bedanken voor het ontwerpen en uitvoeren van deze module. Het was geweldig om haar als mentor te hebben en haar enthousiasme en energie maakten het een zeer prettige en goede cursus. Ik ben erg blij met deze module en de manier waarop Hanneke dit met ons heeft verkend. Hartelijk dank!",
        role: "Cursist"
      }
    },
    en: {
      "Yueran Z.": {
        shortQuote: "With Hanneke's help, I'm no longer afraid of my career after graduation.",
        longQuote: "Hanneke was my instructor for the Career Development module at WUR. She is one of the most enthusiastic instructors I have ever met. She is passionate about helping students develop their personal and professional path. From Hanneke, I learned various strategies for my career development, such as how to present myself on my CV, how to use STAR to describe my achievements in my cover letter, and how to develop my LinkedIn page. More importantly, Hanneke has helped me and other students gain more confidence in ourselves and our future. With her help, I am not afraid to face my career after graduation. I recommend Hanneke to anyone who needs personal or professional advice. You will learn more than you expect!",
        role: "Master Student WUR"
      },
      "Tevreden Cliënt": {
        shortQuote: "Hanneke's enthusiasm and energy made it a very pleasant and good course.",
        longQuote: "First of all, I want to thank Hanneke for designing and implementing this module. It was great to have her as a mentor and her enthusiasm and energy made it a very pleasant and good course. I am very happy with this module and the way Hanneke explored this with us. Thank you very much!",
        role: "Student"
      }
    }
  };

  // Get the correct testimonial based on language
  const getTranslatedTestimonial = () => {
    if (language === 'nl') {
      return {
        quote: expanded ? testimonial.longQuote : testimonial.shortQuote,
        role: testimonial.role
      };
    }
    
    const translationKey = testimonial.name;
    if (translationKey in translatedTestimonials.en) {
      const translatedData = translatedTestimonials.en[translationKey];
      return {
        quote: expanded ? translatedData.longQuote : translatedData.shortQuote,
        role: translatedData.role
      };
    }
    
    // Fallback to original if no translation found
    return {
      quote: expanded ? testimonial.longQuote : testimonial.shortQuote,
      role: testimonial.role
    };
  };
  
  const translatedContent = getTranslatedTestimonial();

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
          "{translatedContent.quote}"
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {testimonial.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translatedContent.role}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={language === 'nl' ? "toon meer" : "show more"}
            sx={{ 
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { language } = useLanguage();
  const { currentUser } = useAuth();
  const t = translations[language]; // Translation function

  // Corrected Feature Helper: Accepts Component Type
  const renderFeature = (IconComponent: React.ElementType, title: string, description: string) => (
      <Grid item xs={12} sm={6} md={4}>
          {/* Using Paper for consistent styling with previous attempts */}
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {/* Render the passed icon component correctly */}
              <IconComponent color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 500 }}>
                  {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                  {description}
              </Typography>
          </Paper>
      </Grid>
  );

  const features = [
    {
      icon: <LightbulbOutlined />,
      title: language === 'nl' ? 'Inzicht' : 'Insight',
      description: language === 'nl' 
        ? 'Krijg duidelijkheid over je waarden, talenten en carrièredoelen.'
        : 'Gain clarity about your values, talents, and career goals.'
    },
    {
      icon: <RouteOutlined />,
      title: language === 'nl' ? 'Actieplan' : 'Action Plan',
      description: language === 'nl'
        ? 'Persoonlijke, op maat gemaakte stappenplannen om je doelen snel te realiseren.'
        : 'Personal, tailored action plans to quickly achieve your goals.'
    },
    {
      icon: <TrendingUpOutlined />,
      title: language === 'nl' ? 'Groei' : 'Growth',
      description: language === 'nl'
        ? 'Samen je zelfkennis en zelfvertrouwen versterken voor duurzaam succes.'
        : 'Together strengthen your self-awareness and confidence for lasting success.'
    }
  ];

  return (
    <>
      {/* Hero Section - Reverted to Text/Buttons Left, Image Right */}
      <Box sx={{ 
        background: `linear-gradient(to bottom, ${theme.palette.background.default}, ${theme.palette.grey[100]})`,
        py: { xs: 6, md: 8 },
        textAlign: { xs: 'center', md: 'left' } // Restore original alignment
      }}>
        <Container maxWidth="lg">
          {/* Reverted Grid structure */}
          <Grid container spacing={6} alignItems="center" direction="row">
            {/* Left Column: Intro Text & CTAs */}
            <Grid item xs={12} md={7}> {/* Reverted to md={7} if needed, or keep md={6} if preferred */}
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary',
                  letterSpacing: '-0.5px',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  textAlign: { xs: 'center', md: 'left' } // Explicitly left-align on larger screens
                }}
              >
                {language === 'nl' ? 'De zoektocht naar jouw droombaan start hier' : 'The search for your dream job starts here.'}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  color: 'text.secondary',
                  fontWeight: 400,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                {language === 'nl' ? 'Kies voor helderheid en daadkracht in je loopbaan, kies voor Sterk aan het Werk.' : 'Choose clarity and decisiveness in your career, choose Sterk aan het Werk.'}
              </Typography>
               <Typography
                 variant="body1"
                 sx={{
                   mb: 3,
                   fontSize: '1.1rem',
                   color: 'text.primary',
                   textAlign: { xs: 'center', md: 'left' }
                 }}
               >
                {language === 'nl' ? 'Kan jij wel wat inzicht in je carrière gebruiken? Samen ontdekken we jouw unieke talenten en persoonlijke missie, zodat je vol zelfvertrouwen je volgende stap kan zetten.' : 'Could you use some insight into your career? Together we discover your unique talents and personal mission, so you can take your next step with confidence.'}
               </Typography>

              {/* CTA Buttons */}
              <Box sx={{ 
                mt: 3,
                display: 'flex', 
                gap: 2, 
                justifyContent: { xs: 'center', md: 'flex-start' }, 
                flexWrap: 'wrap'
              }}>
                  {/* Buttons remain the same */}
                   <Button
                    variant="contained" 
                    color="secondary" 
                    size="large"
                    onClick={() => navigate('/contact')}
                    startIcon={<CommentBank sx={{ color: 'white' }}/>}
                    sx={{ 
                      minWidth: { xs: '100%', sm: '240px' }, 
                      py: 1.8, 
                      px: 3.5, 
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      color: 'white',
                      backgroundColor: theme.palette.secondary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.dark
                      }
                    }}
                  >
                    {language === 'nl' ? 'Maak een afspraak' : 'Book an appointment'}
                  </Button>
                  
                  {!currentUser && (
                    <Button
                      variant="contained" 
                      color="secondary" 
                      size="large"
                      onClick={() => navigate('/login?tab=register')}
                      startIcon={<PersonAdd sx={{ color: 'white' }}/>}
                      sx={{ 
                        minWidth: { xs: '100%', sm: '240px' },
                        py: 1.8,
                        px: 3.5,
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        color: 'white',
                        backgroundColor: theme.palette.secondary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.secondary.dark
                        }
                      }}
                    >
                      {language === 'nl' ? 'Maak een account' : 'Create account'}
                    </Button>
                  )}
                  
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={() => navigate('/diensten')}
                    startIcon={<WorkOutline sx={{ color: 'white' }}/>}
                    sx={{ 
                      minWidth: { xs: '100%', sm: '240px' },
                      py: 1.8,
                      px: 3.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      color: 'white',
                      backgroundColor: theme.palette.secondary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.dark
                      }
                    }}
                  >
                    {t.navigation.diensten}
                  </Button>
              </Box>
            </Grid>

            {/* Right Column: Restore Image */}
             <Grid item xs={12} md={5}> {/* Reverted to md={5} */}
               <Box sx={{ position: 'relative', height: '100%', minHeight: '400px' }}>
                 <Box 
                   component="img"
                   src="/Photo1_me.png"
                   alt={language === 'nl' ? 'Hanneke van Keulen - Coach' : 'Hanneke van Keulen - Coach'}
                   sx={{
                     width: '100%',
                     objectFit: 'cover',
                     display: 'block',
                     borderRadius: '12px',
                     boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                   }}
                 />
               </Box>
             </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Re-inserted Vision Section */}
      <Box sx={{ py: { xs: 5, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Spa color="secondary" sx={{ fontSize: 48, mb: 2 }} />
           <Typography variant="overline" display="block" color="secondary.main" mb={1}>
               {language === 'nl' ? 'MIJN VISIE' : 'MY VISION'}
           </Typography>
           <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
               {language === 'nl' ? 'De Kracht van Coaching' : 'The Power of Coaching'}
           </Typography>
           <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 400 }}>
               {language === 'nl' ? 'Verbind wie je bent met wat je doet' : 'Connect who you are with what you do'}
           </Typography>
           <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: '1.1rem', maxWidth: '700px', mx: 'auto' }}>
               {language === 'nl' ? 'Navigeer door carrièrekeuzes met zelfinzicht. Ik help je jouw waarden, krachten en passies te identificeren en te vertalen naar een loopbaan die écht bij je past.' : 'Navigate career choices with self-insight. I help you identify your values, strengths, and passions and translate them into a career that truly suits you.'}
           </Typography>
           <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', maxWidth: '700px', mx: 'auto' }}>
               {language === 'nl' ? 'Bouw het vertrouwen en de vaardigheden op om professionele uitdagingen aan te gaan en kansen te grijpen die aansluiten bij jouw persoonlijkheid.' : 'Build the confidence and skills to tackle professional challenges and seize opportunities aligned with your personality.'}
           </Typography>
        </Container>
      </Box>

      {/* === Features/"How I Help" Section === */}
      <Box sx={{ py: { xs: 6, md: 9 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 500, mb: 4 }}>
             {language === 'nl' ? 'WAT JE KUNT VERWACHTEN' : 'WHAT CAN YOU EXPECT'}
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {/* Call renderFeature with Component Names */}
            {renderFeature(Spa, language === 'nl' ? 'Heldere Inzichten' : 'Clear Insights', language === 'nl' ? 'Krijg duidelijkheid over je waarden, talenten en carrièredoelen.' : 'Gain clarity about your values, talents, and career goals.')}
            {renderFeature(QueryStats, language === 'nl' ? 'Concrete Strategieën' : 'Actionable Strategies', language === 'nl' ? 'Persoonlijke, op maat gemaakte stappenplannen om je doelen snel te realiseren.' : 'Personal, tailored action plans to quickly achieve your goals.')}
            {renderFeature(EmojiEvents, language === 'nl' ? 'Zelfverzekerde Groei' : 'Confident Growth', language === 'nl' ? 'Samen je zelfkennis en zelfvertrouwen versterken voor duurzaam succes.' : 'Together strengthen your self-awareness and confidence for lasting success.')}
          </Grid>
        </Container>
      </Box>

      {/* === Services Section === */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 9 }, mb: 8 }}>
        <Typography 
          variant="h2" 
          align="center" 
          sx={{ 
            mb: 3,
            fontFamily: theme.typography.h2.fontFamily
          }}
        >
          {language === 'nl' ? 'Ontdek mijn diensten' : 'Discover My Services'}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {language === 'nl' 
            ? 'Hieronder vind je een overzicht van hoe ik je specifiek kan ondersteunen:' 
            : 'Below you\'ll find an overview of how I can specifically support you:'}
        </Typography>
        
        {/* Services Cards - New Design from screenshot */}
        <Grid container spacing={4} justifyContent="center">
          {/* LOOPBAAN COACHING */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
              elevation={2}
            >
              <Box sx={{ 
                bgcolor: theme.palette.primary.light,
                p: 4, 
                textAlign: 'center'
              }}>
                <PersonOutlined sx={{ fontSize: 50, color: 'white' }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mt: 2, color: 'white' }}>
                  {language === 'nl' ? 'INDIVIDUELE LOOPBAANCOACHING' : 'CAREER COACHING'}
                </Typography>
              </Box>
              <Box sx={{ p: 4 }}>
                <Typography variant="body1" paragraph>
                  {language === 'nl' 
                    ? 'Persoonlijke begeleiding om jouw unieke talenten te ontdekken en maximaal te benutten in je loopbaan.'
                    : 'Personal guidance to discover your unique talents and utilize them to the fullest in your career.'}
                </Typography>
                <Button 
                  component={Link} 
                  to="/diensten/individuele-coaching" 
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  endIcon={<ArrowForward />}
                >
                  {language === 'nl' ? 'Meer informatie' : 'More information'}
                </Button>
              </Box>
            </Card>
          </Grid>
          
          {/* WORKSHOPS & GROEPSTRAININGEN */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
              elevation={2}
            >
              <Box sx={{ 
                bgcolor: theme.palette.primary.light,
                p: 4, 
                textAlign: 'center'
              }}>
                <EventOutlined sx={{ fontSize: 50, color: 'white' }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mt: 2, color: 'white' }}>
                  {language === 'nl' ? 'WORKSHOPS & GROEPSTRAININGEN' : 'WORKSHOPS & GROUP TRAININGS'}
                </Typography>
              </Box>
              <Box sx={{ p: 4 }}>
                <Typography variant="body1" paragraph>
                  {language === 'nl' 
                    ? 'Inspirerende bijeenkomsten en interactieve sessies voor persoonlijke en professionele groei.'
                    : 'Inspiring meetings and interactive sessions for personal and professional growth.'}
                </Typography>
                <Button 
                  component={Link} 
                  to="/diensten/workshops" 
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  endIcon={<ArrowForward />}
                >
                  {language === 'nl' ? 'Meer informatie' : 'More information'}
                </Button>
              </Box>
            </Card>
          </Grid>
          
          {/* LESGEVEN & PUBLIC SPEAKING */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
              elevation={2}
            >
              <Box sx={{ 
                bgcolor: theme.palette.primary.light,
                p: 4, 
                textAlign: 'center'
              }}>
                <SchoolOutlined sx={{ fontSize: 50, color: 'white' }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mt: 2, color: 'white' }}>
                  {language === 'nl' ? 'LESGEVEN & PUBLIC SPEAKING' : 'TEACHING & PUBLIC SPEAKING'}
                </Typography>
              </Box>
              <Box sx={{ p: 4 }}>
                <Typography variant="body1" paragraph>
                  {language === 'nl' 
                    ? 'Professionele kennisoverdracht en inspirerende presentaties voor diverse doelgroepen.'
                    : 'Professional knowledge transfer and inspiring presentations for diverse audiences.'}
                </Typography>
                <Button 
                  component={Link} 
                  to="/diensten/lesgeven" 
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  endIcon={<ArrowForward />}
                >
                  {language === 'nl' ? 'Meer informatie' : 'More information'}
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
        
        {/* Original Service Cards - Keep for backward compatibility or remove if not needed */}
        {/* <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%',
                border: '1px solid rgba(0,0,0,0.08)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
              elevation={0}
            >
              <Box 
                sx={{ 
                  height: 200, 
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default'
                }}
              >
                <Typography variant="h4" align="center" color="primary.main" sx={{ fontFamily: theme.typography.h4.fontFamily }}>
                  {language === 'nl' ? 'Individuele Coaching' : 'Individual Coaching'}
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body1" paragraph>
                  {language === 'nl'
                    ? 'Krijg duidelijkheid over je waarden, talenten en carrièredoelen.'
                    : 'Gain clarity about your values, talents, and career goals.'}
                </Typography>
                <Button 
                  component={Link} 
                  to="/diensten/individuele-coaching" 
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {language === 'nl' ? 'Lees meer' : 'Read more'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%',
                border: '1px solid rgba(0,0,0,0.08)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
              elevation={0}
            >
              <Box 
                sx={{ 
                  height: 200, 
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default'
                }}
              >
                <Typography variant="h4" align="center" color="primary.main" sx={{ fontFamily: theme.typography.h4.fontFamily }}>
                  {language === 'nl' ? 'Workshops' : 'Workshops'}
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body1" paragraph>
                  {language === 'nl'
                    ? 'Interactieve groepssessies voor persoonlijke en professionele groei.'
                    : 'Interactive group sessions for personal and professional growth.'}
                </Typography>
                <Button 
                  component={Link} 
                  to="/diensten/workshops" 
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {language === 'nl' ? 'Lees meer' : 'Read more'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%',
                border: '1px solid rgba(0,0,0,0.08)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
              elevation={0}
            >
              <Box 
                sx={{ 
                  height: 200, 
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default'
                }}
              >
                <Typography variant="h4" align="center" color="primary.main" sx={{ fontFamily: theme.typography.h4.fontFamily }}>
                  {language === 'nl' ? 'Lesgeven & Public Speaking' : 'Teaching & Public Speaking'}
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body1" paragraph>
                  {language === 'nl'
                    ? 'Kennisoverdracht op een inspirerende, betrokken en enthousiaste wijze.'
                    : 'Knowledge transfer in an inspiring, engaged, and enthusiastic way.'}
                </Typography>
                <Button 
                  component={Link} 
                  to="/diensten/lesgeven" 
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {language === 'nl' ? 'Lees meer' : 'Read more'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid> */}
      </Container>

      {/* === Testimonials Section === */}
      <Box sx={{ py: { xs: 6, md: 9 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
             {language === 'nl' ? 'Testimonials' : 'What Others Are Saying'}
          </Typography>
           <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                {language === 'nl' 
                  ? 'Hoor van andere professionals die meer duidelijkheid en richting vonden via Sterk aan het Werk:'
                  : 'Hear from other professionals who found clarity and direction through Sterk aan het Werk:'}
           </Typography>
          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((testimonial, index) => (
              <Grid item key={index} xs={12} md={6}>
                <ExpandableTestimonial testimonial={testimonial} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Premium features highlight section */}
      {!currentUser && (
        <Box sx={{ py: 5, bgcolor: theme.palette.primary.light, color: 'white' }}>
          <Container maxWidth="lg">
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Chip 
                icon={<Star />} 
                label={language === 'nl' ? "PREMIUM VOORDELEN" : "PREMIUM BENEFITS"} 
                color="secondary" 
                sx={{ mb: 2, fontWeight: 'bold', color: 'white' }} 
              />
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                {language === 'nl' ? 'Ontgrendel je volledige potentieel' : 'Unlock your full potential'}
              </Typography>
              <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', mb: 3, opacity: 0.9 }}>
                {language === 'nl' 
                  ? 'Maak een gratis account aan om deze premium voordelen te ontdekken' 
                  : 'Create a free account to discover these premium benefits'}
              </Typography>
            </Box>
            
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <LockOpen color="secondary" sx={{ fontSize: 50, mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {language === 'nl' ? 'Exclusieve content' : 'Exclusive content'}
                    </Typography>
                    <Typography variant="body2">
                      {language === 'nl' 
                        ? 'Toegang tot tools en tips die niet publiekelijk beschikbaar zijn' 
                        : 'Access tools and tips that are not publicly available'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Assessment color="secondary" sx={{ fontSize: 50, mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {language === 'nl' ? 'Persoonlijke groei' : 'Personal growth'}
                    </Typography>
                    <Typography variant="body2">
                      {language === 'nl' 
                        ? 'Ontwikkel jezelf met volledig gepersonaliseerde coaching' 
                        : 'Develop yourself with fully personalized coaching'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <WorkOutline color="secondary" sx={{ fontSize: 50, mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {language === 'nl' ? 'Professionele hulpmiddelen' : 'Professional tools'}
                    </Typography>
                    <Typography variant="body2">
                      {language === 'nl' 
                        ? 'CV templates, sollicitatietips en carrière-ontwikkelingsstrategieën' 
                        : 'CV templates, job application tips and career development strategies'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button 
                variant="contained" 
                color="secondary" 
                size="large" 
                onClick={() => navigate('/login?tab=register')}
                startIcon={<PersonAdd />}
                sx={{ 
                  py: 1.5, 
                  px: 5, 
                  borderRadius: 2, 
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  boxShadow: theme.shadows[4]
                }}
              >
                {language === 'nl' ? 'Maak nu een gratis account' : 'Create a free account now'}
              </Button>
              <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
                {language === 'nl' 
                  ? 'Al een account? Log in om verder te gaan naar premium content' 
                  : 'Already have an account? Log in to access premium content'}
              </Typography>
            </Box>
          </Container>
        </Box>
      )}
    </>
  );
};

export default Home; 